<template>
	<div>
		<b-modal
			centered
			size="lg"
			id="contact-modal"
			modal-class="mg-contact-modal mg-contact-modal--simple"
			hide-header
			hide-footer
		>
			<!-- In the sign up flow the member wants help with their account, not a demo,
				so Figma swaps the employer pitch for a support address plus a short
				register-interest form. -->
			<div class="cs">
				<header class="cs__head">
					<h2 class="cs__title">Contact us</h2>
					<button class="cs__close" @click="cancel" aria-label="Close">
						<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
							<path d="M6 6l12 12M18 6L6 18"/>
						</svg>
					</button>
				</header>

				<p v-if="simple" class="cs__help">
					For help with creating your account email us at<br />
					<a href="mailto:help@moneyguided.com">help@moneyguided.com</a>
				</p>

				<div v-if="state === 'success'" class="cs__success">
					<p>Thanks for your interest.<br />We'll be in touch soon.</p>
					<button class="cs__submit cs__submit--close" @click="cancel">Close</button>
				</div>

				<form v-else class="cs__body" @submit.prevent="sendEnquiry" novalidate>
					<h3 class="cs__sub">Register interest</h3>
					<p class="cs__lede">
						To find out more about how Money Guided can help your employees, register
						your interest and our team will be in touch.
					</p>

					<label class="cs__field">
						<span>Your name*</span>
						<input type="text" v-model="enquiry.name" autocomplete="name" />
					</label>

					<label class="cs__field">
						<span>Email address*</span>
						<input type="email" v-model="enquiry.email" autocomplete="email" />
						<small>We won't share your email with anyone else.</small>
					</label>

					<label class="cs__field">
						<span>Company name*</span>
						<input type="text" v-model="enquiry.company" autocomplete="organization" />
					</label>

					<label class="cs__field">
						<span>Job title</span>
						<input type="text" v-model="enquiry.jobTitle" autocomplete="organization-title" />
					</label>

					<p v-if="error" class="cs__error" role="alert">{{ error }}</p>

					<button type="submit" class="cs__submit" :disabled="!simpleReady || state === 'sending'">
						<span>{{ state === 'sending' ? 'Sending...' : 'Submit' }}</span>
						<span aria-hidden="true">&rarr;</span>
					</button>
				</form>
			</div>

		</b-modal>
	</div>
</template>

<script>
export default {
	data() {
		return {
			state: 'idle',
			error: '',
			enquiry: this.empty(),
		};
	},
	computed: {
		// Same gate as the stripped back nav in Menu.vue: meta.api marks the sign up routes.
		simple() {
			return Boolean(this.$route.meta && this.$route.meta.api);
		},
		simpleReady() {
			const e = this.enquiry;
			return Boolean(e.name.trim() && e.email.trim() && e.company.trim());
		},
	},
	methods: {
		empty() {
			return {
				name: '', email: '', subject: '', message: '',
				company: '', jobTitle: '',
			};
		},
		cancel() {
			this.$bvModal.hide('contact-modal');
			setTimeout(() => {
				this.state = 'idle';
				this.error = '';
			}, 300);
		},
		validate() {
			if (!this.enquiry.name.trim()) return 'Please tell us your name.';
			if (!this.enquiry.email.trim()) return 'Please provide an email address.';
			if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.enquiry.email)) return 'That email address doesn\'t look right.';
			if (!this.enquiry.company.trim()) return 'Please tell us your company.';
			return '';
		},
		sendEnquiry() {
			const err = this.validate();
			if (err) { this.error = err; return; }
			this.error = '';
			this.state = 'sending';

			this.enquiry.message =
				`Name: ${this.enquiry.name}. Email: ${this.enquiry.email}. ` +
				`Company: ${this.enquiry.company}. Job title: ${this.enquiry.jobTitle}.` +
				(this.simple ? ' Registered interest from the sign up flow.' : '');

			this.$http.post('https://api.sysodev.link/Enquiries', this.enquiry)
				.then(() => {
					this.enquiry = this.empty();
					this.state = 'success';
				})
				.catch(() => {
					this.state = 'idle';
					this.error = 'Something went wrong sending your enquiry. Please try again.';
				});
		},
	},
};
</script>

<style>
/* Modal shell overrides (unscoped so they reach the b-modal portal) */
.mg-contact-modal .modal-dialog {
	max-width: 920px;
}
.mg-contact-modal .modal-content {
	border-radius: 24px !important;
	border: 0 !important;
	overflow: hidden;
	box-shadow: 0 40px 120px rgba(26, 18, 48, 0.4) !important;
}
.mg-contact-modal .modal-body {
	padding: 0 !important;
}
.mg-contact-modal .modal-header,
.mg-contact-modal .modal-footer { display: none !important; }

/* Figma's sign up variant is a narrow single column */
.mg-contact-modal--simple .modal-dialog { max-width: 600px; }
</style>

<style scoped>
/* ---------- SIGN UP VARIANT ---------- */
.cs {
	background: #fff;
	color: var(--mg-ink);
	font-family: 'Montserrat', sans-serif;
}
.cs__head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 20px 32px;
	border-bottom: 1px solid var(--mg-line-dark);
}
.cs__title {
	font-size: 1.25rem;
	font-weight: 800;
	margin: 0;
}
.cs__close {
	flex: none;
	width: 36px;
	height: 36px;
	border: 0;
	border-radius: 50%;
	background: rgba(26, 18, 48, 0.06);
	color: var(--mg-ink);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
}
.cs__close:hover { background: rgba(26, 18, 48, 0.12); }

.cs__help {
	margin: 0;
	padding: 22px 32px;
	border-bottom: 1px solid var(--mg-line-dark);
	font-size: 0.95rem;
	line-height: var(--lh-body);
}
.cs__help a {
	color: var(--mg-ink);
	font-weight: 700;
	text-decoration: underline;
}

/* Figma puts the register interest form on the periwinkle to mauve wash, with the
   header and help band left white above it */
.cs__body {
	padding: 24px 32px 32px;
	background: linear-gradient(170deg, #adbee2 0%, #c6cae4 45%, #d3c8d6 100%);
}
.cs__sub {
	font-size: 1rem;
	font-weight: 800;
	margin: 0 0 8px;
}
/* --mg-mute is tuned for white, and washes out on the gradient */
.cs__lede {
	font-size: 0.9rem;
	line-height: var(--lh-body);
	color: rgba(26, 18, 48, 0.75);
	margin: 0 0 24px;
}

.cs__field { display: block; margin-bottom: 20px; }
.cs__field span {
	display: block;
	font-size: 0.9rem;
	margin-bottom: 8px;
}
.cs__field input {
	width: 100%;
	font-family: inherit;
	font-size: 0.95rem;
	color: var(--mg-ink);
	background: #fff;
	border: 1px solid transparent;
	border-radius: var(--radius-sm);
	padding: 14px 16px;
	outline: none;
	transition: border-color 200ms var(--ease-out), box-shadow 200ms var(--ease-out);
}
.cs__field input:focus {
	border-color: var(--mg-peach-deep);
	box-shadow: 0 0 0 4px rgba(244, 184, 154, 0.2);
}
.cs__field small {
	display: block;
	margin-top: 6px;
	font-size: var(--fs-tiny);
	color: rgba(26, 18, 48, 0.7);
}

.cs__submit {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: var(--sp-3);
	width: 100%;
	min-height: 56px;
	border: 0;
	border-radius: var(--radius-pill);
	background: var(--mg-ink);
	color: var(--mg-white);
	font-family: inherit;
	font-weight: 700;
	font-size: 0.95rem;
	cursor: pointer;
}
.cs__submit:disabled {
	background: rgba(26, 18, 48, 0.25);
	cursor: not-allowed;
}
.cs__submit:hover:not(:disabled) { background: var(--mg-ink-3); }

.cs__success {
	padding: 80px 32px;
	text-align: center;
}
.cs__success p {
	font-weight: 700;
	line-height: var(--lh-body);
	margin: 0 0 28px;
}
.cs__submit--close {
	max-width: 320px;
	margin: 0 auto;
}

@media (max-width: 575px) {
	.cs__head, .cs__help { padding-left: 20px; padding-right: 20px; }
	.cs__body { padding: 20px 20px 24px; }
	.cs__success { padding: 48px 20px; }
}

.cs__error {
	margin: 0 0 var(--sp-3);
	font-size: var(--fs-small);
	color: var(--mg-onb-error);
}
</style>
